/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: pickupGroupsActions.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the PBX Applications module.
────────────────────────────────────────────────────────────────*/

import type { NavigateFunction } from 'react-router-dom';
import { buildApiUrl, getApiHeaders } from '../../config/api';

/**
 * Shared helpers for Pickup Groups module actions.
 */

/**
 * Build the URL used to duplicate a Pickup Group in the form.
 * Pattern: /pbx/applications/pickup-groups/new?duplicate={id}
 */
export const getPickupGroupDuplicateUrl = (id: string | number): string =>
  `/pbx/applications/pickup-groups/new?duplicate=${id}`;

/**
 * Navigate to the form in duplicate mode for the given pickup group id.
 */
export const navigateToPickupGroupDuplicate = (
  navigate: NavigateFunction,
  id: string | number,
): void => {
  navigate(getPickupGroupDuplicateUrl(id));
};

/**
 * Low-level API helper to delete a Pickup Group by id.
 * UI components are responsible for showing notifications and handling navigation/refresh.
 */
export const deletePickupGroupById = async (id: string | number): Promise<void> => {
  const response = await fetch(
    buildApiUrl(`/api/telephony/pickup-groups/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders(),
    },
  );

  if (!response.ok) {
    let message = 'Failed to delete pickup group';
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors and use default message
    }
    throw new Error(message);
  }
};


